import { BufferGeometry, InstancedMesh, Material, Mesh, Object3D, Texture } from "three";

/** Textures referenced by a material (map, normalMap, envMap...) */
export function getMaterialTextures(material: Material): Texture[] {
  return Object.values(material).filter((v): v is Texture => !!(v as Texture | null)?.isTexture);
}

/**
 * Frees the GPU resources (geometries, materials and their textures, and the instance buffers of
 * instanced meshes) of an object and its descendants. It does not remove the object from its parent.
 * @param root The object to traverse
 * @param keep Resources that are shared or cached and must not be disposed
 */
export function disposeObject3D(root: Object3D, keep?: ReadonlySet<unknown>): void {
  const geometries = new Set<BufferGeometry>();
  const materials = new Set<Material>();
  const textures = new Set<Texture>();
  const instancedMeshes: InstancedMesh[] = [];

  root.traverse((obj) => {
    if ((obj as InstancedMesh).isInstancedMesh) instancedMeshes.push(obj as InstancedMesh);

    const { geometry, material } = obj as Partial<Mesh>;
    if (geometry) geometries.add(geometry);
    if (!material) return;

    (Array.isArray(material) ? material : [material]).forEach((m) => {
      materials.add(m);
      getMaterialTextures(m).forEach((t) => textures.add(t));
    });
  });

  const notKept = (r: unknown) => !keep?.has(r);

  // Only frees the instance buffers: the geometry and materials go through `keep` below
  instancedMeshes.forEach((m) => m.dispose());

  [...geometries].filter(notKept).forEach((g) => g.dispose());
  [...materials].filter(notKept).forEach((m) => m.dispose());
  [...textures].filter(notKept).forEach((t) => t.dispose());
}
